import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {AbstractControl, FormControl, FormGroup} from "@angular/forms";
import {FormFieldSelectOptions, FormFieldType, FormValidator, ImageDownloadDto} from "../../api";
import {FileService} from "../../services/file-service/file.service";
import {Subscription} from "rxjs";
import {ImageLightboxComponent, ImageLightboxData} from "../../dialog/image-lightbox/image-lightbox.component";
import {MatDialog} from "@angular/material/dialog";


@Component({
  selector: 'ui-dynamic-form-field',
  templateUrl: './dynamic-form-field.component.html',
  styleUrl: './dynamic-form-field.component.scss'
})
export class DynamicFormFieldComponent implements OnInit, OnDestroy{

  @Input() entry: any; // Hotfix, später typisieren
  @Input({ required: true }) group!: FieldGroup;
  @Input() EntryImgId : string = "";
  @Input() IsNew : boolean = false;
  /** When true, hidden fields are rendered (with a "not shown in app" hint) instead of skipped. */
  @Input() showHidden = false;
  @Output() triggerUpload = new EventEmitter<string>();
  @Output() triggerReload = new EventEmitter<string>();
  isLoadingImg = true;

  responsiveOptions = [
    {
      breakpoint: '1024px',
      numVisible: 3,
      numScroll: 3,
    },
    {
      breakpoint: '768px',
      numVisible: 2,
      numScroll: 2,
    },
    {
      breakpoint: '560px',
      numVisible: 1,
      numScroll: 1,
    },
  ];

  Img : ImageDownloadDto[] | undefined = []

  get type() { return this.group.get('type')?.value }
  get selectOptions() { return this.group.get('selectOptions')?.value }
  get uniqueKey() { return this.group.get('uniqueKey')?.value as string }
  get label(): string { return this.group.get('label')?.value as string; }
  get hidden(): boolean { return this.group.get('hidden')?.value || false; }
  get valueCtrl(): AbstractControl | null { return this.group.get('value'); }
  get hint(): string | null { return this.group.get('hint')?.value ?? null; }

  fileServiceSub?: Subscription;
  fileServiceEmptySub?: Subscription;

  constructor(
    private fileService : FileService,
    private dialog : MatDialog

  ) {
  }

  ngOnInit() {
  this.isLoadingImg = true;

  // 1) Primärquelle: direkt aus dem Entry (API liefert imgDownloadUrls)
  const dict = this.entry?.imgDownloadUrls as { [key: string]: ImageDownloadDto[] } | undefined;

  this.Img = dict
    ? Object.entries(dict).flatMap(([galleryKey, images]) =>
        (images ?? []).map(img => ({
          ...img,
          galleryKey: img.galleryKey ?? galleryKey
        }))
      )
    : [];

  this.isLoadingImg = false;

  // 2) Optional: weiterhin auf Service hören (z.B. nach Upload live refresh)
  this.fileServiceSub = this.fileService.entryFileSubject.subscribe(res => {
    const fromService = res.get(this.EntryImgId);
    if (fromService && fromService.length) {
      this.Img = fromService;
    }
  });
}

  ngOnDestroy() {
    this.fileServiceSub?.unsubscribe();
    this.fileServiceEmptySub?.unsubscribe();
  }

  getImgObj(value : string | undefined){
    if (value == undefined) return []
    let res : ImageLightboxData[] = [];
    let galleryImg = this.getGallery(value);
    if(galleryImg == undefined) return [];
    galleryImg.forEach((i:ImageDownloadDto) => {
      let url = i.signedUrl;
      if(url == undefined) return;
      res.push({
        image: url,
        fileId: i.id || ""
      })
    })
    return res;
  }

  openLightbox(file : ImageLightboxData){
    const allImages = this.getImgObj(this.uniqueKey);
    
    this.dialog.open(ImageLightboxComponent, {
      width: '100vw',
      maxWidth: '100vw',
      height: '100vh',
      maxHeight: '100vh',
      panelClass: 'image-lightbox-dialog',
      data: {
        ...file,
        allImages: allImages,
        allowDelete: true
      }
    })

    this.dialog.afterAllClosed.subscribe(()=> {
      this.triggerReload.emit()
    })
  }

  getGallery(galleryKey : string){
    if (this.Img == undefined) return [];
    return this.Img?.filter(i => i.galleryKey == galleryKey)
  }

  getErrorMessages(): string[] {
    const control = this.valueCtrl;
    const validatorConfigs = this.group.get('validators')?.value || [];
    if (!control || !control.errors) return [];

    const messages: string[] = [];
    for (const key in control.errors) {
      const match = validatorConfigs.find((v: any) =>
        v.type.toLowerCase().includes(key.toLowerCase())
      );
      if (match && match.message) messages.push(match.message);
    }

    return messages;
  }

  shouldShowNavigators(images: ImageLightboxData[]): boolean {
    if (images.length === 0) return false;
    
    const windowWidth = window.innerWidth;
    let numVisible = 3;
    
    if (windowWidth < 560) {
      numVisible = 1;
    } else if (windowWidth < 768) {
      numVisible = 2;
    }
    
    return images.length > numVisible;
  }

  protected readonly FormFieldType = FormFieldType;
}

export type FieldGroup = FormGroup<{
  id: FormControl<string>;
  type: FormControl<string>;
  label: FormControl<string>;
  hidden: FormControl<boolean>;
  validators: FormControl<Array<FormValidator>>
  uniqueKey: FormControl<string>;
  value: FormControl<any>;
  selectOptions: FormControl<FormFieldSelectOptions>;
}>;
